import { useState, useEffect } from "react";
import { Grid, CardContent, Typography, Paper, Box, Button, TextField, ButtonGroup } from "@mui/material";
import { DraggableListItem } from "src/components/DraggableListItem/DraggableListItem";
import { CustomerForm } from "./CustomerForm";
import { CustomerTypeForm } from "./CustomerTypeForm";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { useCancellablePromise } from "src/API/useCancellablePromise";
import { ICustomer, ICustomerType } from "@vmosolutions/oc-interfaces";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import AddIcon from "@mui/icons-material/Add";
import LoadingWrapper from "src/components/LoadingWrapper/LoadingWrapper";
import { initialCustomerValues } from "./CustomerForm";
interface IManageCustomersProps {
  id: string;
  setActiveModal: (activeModal: IActiveModal) => void;
  height: number;
}

interface ICustomerGroup {
  label: string;
  options: ICustomer[];
}
const sortCustomersAlphabetically = (array: ICustomer[]): any => {
  return array.sort((a, b) => {
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  });
};

export const formatCustomerList = (customers: ICustomer[]): ICustomerGroup[] => {
  if (customers.length > 0) {
    type GroupTitle = "Active" | "Inactive";
    const customerGroups: { [key in GroupTitle]: ICustomer[] } = {
      Active: [],
      Inactive: [],
    };

    customers.forEach((cus: ICustomer) => {
      const groupTitle: GroupTitle = cus.active ? "Active" : "Inactive";
      customerGroups[groupTitle].push(cus);
    });

    return [
      { label: "Active", options: customerGroups.Active },
      { label: "Inactive", options: customerGroups.Inactive },
    ];
  } else {
    return [];
  }
};

export const ManageCustomers = (props: IManageCustomersProps): JSX.Element => {
  const { setActiveModal, height } = props;
  const { cancellablePromise } = useCancellablePromise();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showCustomerTypeModal, setShowCustomerTypeModal] = useState<boolean>(false);
  const [customers, setCustomers] = useState<ICustomer[]>([]);
  const [customerFilter, setCustomerFilter] = useState<string>("");
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer | null>(null);
  const [customerTypeOptions, setCustomerTypeOptions] = useState<ICustomerType[]>([]);

  useEffect(() => {
    const getCustomersAction = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.Get<ICustomer[]>("/customers", undefined, cancellablePromise);
      setIsLoading(false);
      if (Array.isArray(response)) setCustomers(sortCustomersAlphabetically(response));
    };
    getCustomersAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateCustomersList = (cus: ICustomer, mode: "create" | "update" | "delete"): void => {
    const newCustomers = [...customers];
    const index = newCustomers.findIndex((customer: ICustomer) => customer._id === cus._id);
    switch (true) {
      case cus._id && mode === "create":
        newCustomers.push(cus);
        setSelectedCustomer(cus);
        break;
      case cus._id && mode === "update" && index > -1:
        newCustomers[index] = cus;
        setSelectedCustomer(cus);
        break;
      case cus._id && mode === "delete" && index > -1:
        newCustomers.splice(index, 1);
        setSelectedCustomer(initialCustomerValues);
        break;
      default:
      // code block
    }
    setCustomers(newCustomers);
  };

  useEffect(() => {
    const getTypes = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.Get<ICustomerType[]>("/customertypes", undefined, cancellablePromise);
      if (Array.isArray(response))
        setCustomerTypeOptions(
          response.sort((a, b) => {
            return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
          })
        );
    };
    getTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateCustomerTypeList = (cusType: ICustomerType, mode: "create" | "update" | "delete"): void => {
    const newCustomerTypeOptions = [...customerTypeOptions];
    const index = newCustomerTypeOptions.findIndex((customerType: ICustomerType) => customerType._id === cusType._id);
    switch (true) {
      case cusType._id && mode === "create":
        newCustomerTypeOptions.push(cusType);
        break;
      case cusType._id && mode === "update" && index > -1:
        newCustomerTypeOptions[index] = cusType;
        break;
      case cusType._id && mode === "delete" && index > -1:
        newCustomerTypeOptions.splice(index, 1);
        break;
      default:
    }
    setCustomerTypeOptions(newCustomerTypeOptions);
  };

  return (
    <CardContent>
      <Grid container columnSpacing={3}>
        <Grid item xs={3} sx={{ width: "100%" }}>
          <Paper elevation={4} sx={{ pl: 1, pb: 1, width: "100%", height: `calc(${height}px - 40px)` }}>
            {isLoading ? (
              <Box sx={{ pt: 5 }}>
                <LoadingWrapper message="Loading Customers" />
              </Box>
            ) : (
              <>
                <Grid item xs={12} sx={{ fontSize: "0.8em", alignItems: "center", display: "flex", p: 1 }}>
                  <ButtonGroup size="small" variant="contained" orientation="vertical" fullWidth>
                    <Button color="success" onClick={() => setSelectedCustomer(null)}>
                      <AddIcon fontSize="small" />
                      Add New Customer
                    </Button>
                    <Button color="info" onClick={() => setShowCustomerTypeModal(true)}>
                      Manage Customer Types
                    </Button>
                  </ButtonGroup>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    placeholder="Filter by name or customer type"
                    variant="outlined"
                    size="small"
                    fullWidth
                    onChange={(e) => setCustomerFilter(e.target.value)}
                  />
                </Grid>
                <Grid container item xs={12}>
                  <Grid item sx={{ width: "100%", overflowY: "scroll", height: `calc(${height}px - 170px)` }}>
                    {customers
                      .filter((customer) => {
                        const filter = customerFilter.toLowerCase().replace(/[^a-zA-Z0-9.\s]/g, "");
                        return (
                          customer.name.toLowerCase().includes(filter) ||
                          customer.customerType.title.toLowerCase().includes(filter)
                        );
                      })
                      .map((customer: ICustomer) => {
                        const isActive = !!selectedCustomer && customer._id === selectedCustomer._id;

                        return (
                          <DraggableListItem
                            key={customer._id}
                            active={isActive ? ("true" as unknown as boolean) : (undefined as unknown as boolean)}
                            sx={{
                              cursor: "pointer",
                              alignItems: "center",
                              justifyContent: "space-between",
                              display: "flex",
                            }}
                            onClick={() => setSelectedCustomer(customer)}
                          >
                            <Box>{customer.name}</Box>
                            <Box>{customer.customerType.title}</Box>
                          </DraggableListItem>
                        );
                      })}
                  </Grid>
                </Grid>
              </>
            )}
          </Paper>
        </Grid>
        <Grid container item xs={9}>
          <Grid item xs={12} sx={{ display: "flex", alignItems: "center", minHeight: 50 }}>
            <Typography variant="h6">Manage Customers</Typography>
          </Grid>

          <Grid item xs={12} sx={{ height: `calc(${height}px - 100px)`, overflowY: "scroll" }}>
            <CustomerForm
              selectedCustomer={selectedCustomer}
              customerTypeOptions={customerTypeOptions}
              updateCustomerList={updateCustomersList}
              setActiveModal={setActiveModal}
            />
            <CustomerTypeForm
              showCustomerTypeModal={showCustomerTypeModal}
              customerTypeOptions={customerTypeOptions}
              setShowCustomerTypeModal={setShowCustomerTypeModal}
              updateCustomerTypeList={updateCustomerTypeList}
              setActiveModal={setActiveModal}
            />
          </Grid>
        </Grid>
      </Grid>
    </CardContent>
  );
};
